import { Group, Question, QuestionType } from '../../components/types';

interface UseQuestionsManagementProps {
  groups: Group[];
  setGroups: (groups: Group[]) => void;
  saveToHistory: () => void;
  generateId: () => string;
}

export function useQuestionsManagement({
  groups,
  setGroups,
  saveToHistory,
  generateId,
}: UseQuestionsManagementProps) {
  const addQuestion = (groupId: string, sectionId: string, insertIndex?: number) => {
    saveToHistory();
    setGroups(
      groups.map((group) => {
        if (group.id === groupId) {
          return {
            ...group,
            sections: group.sections.map((section) => {
              if (section.id === sectionId) {
                const newQuestion: Question = {
                  id: generateId(),
                  title: 'Nova Pergunta',
                  answerType: QuestionType.SHORT_TEXT,
                  order: insertIndex !== undefined ? insertIndex : section.questions.length,
                  isRequired: false,
                  showInSimplifiedReport: false,
                  hasHint: false,
                  hasDisplayRule: false,
                  tags: [],
                };

                let updatedQuestions: Question[];
                if (insertIndex !== undefined) {
                  updatedQuestions = [...section.questions];
                  updatedQuestions.splice(insertIndex, 0, newQuestion);
                  updatedQuestions = updatedQuestions.map((q, idx) => ({ ...q, order: idx }));
                } else {
                  updatedQuestions = [...section.questions, newQuestion];
                }

                return { ...section, questions: updatedQuestions };
              }
              return section;
            }),
          };
        }
        return group;
      })
    );
  };

  const editQuestion = (
    groupId: string,
    sectionId: string,
    questionId: string,
    updates: Partial<Question>
  ) => {
    saveToHistory();
    setGroups(
      groups.map((group) => {
        if (group.id === groupId) {
          return {
            ...group,
            sections: group.sections.map((section) => {
              if (section.id === sectionId) {
                return {
                  ...section,
                  questions: section.questions.map((question) =>
                    question.id === questionId ? { ...question, ...updates } : question
                  ),
                };
              }
              return section;
            }),
          };
        }
        return group;
      })
    );
  };

  const removeQuestion = (groupId: string, sectionId: string, questionId: string) => {
    saveToHistory();
    setGroups(
      groups.map((group) => {
        if (group.id === groupId) {
          return {
            ...group,
            sections: group.sections.map((section) => {
              if (section.id === sectionId) {
                const filteredQuestions = section.questions.filter((q) => q.id !== questionId);
                return {
                  ...section,
                  questions: filteredQuestions.map((question, index) => ({
                    ...question,
                    order: index,
                  })),
                };
              }
              return section;
            }),
          };
        }
        return group;
      })
    );
  };

  const duplicateQuestion = (groupId: string, sectionId: string, questionId: string) => {
    saveToHistory();
    setGroups(
      groups.map((group) => {
        if (group.id === groupId) {
          return {
            ...group,
            sections: group.sections.map((section) => {
              if (section.id === sectionId) {
                const questionToDuplicate = section.questions.find((q) => q.id === questionId);
                if (!questionToDuplicate) return section;

                const duplicatedQuestion: Question = {
                  ...JSON.parse(JSON.stringify(questionToDuplicate)),
                  id: generateId(),
                  title: `${questionToDuplicate.title} (Cópia)`,
                  order: section.questions.length,
                };

                return {
                  ...section,
                  questions: [...section.questions, duplicatedQuestion],
                };
              }
              return section;
            }),
          };
        }
        return group;
      })
    );
  };

  return {
    addQuestion,
    editQuestion,
    removeQuestion,
    duplicateQuestion,
  };
}
